import React, { Suspense, useEffect, useState, lazy, memo } from 'react';
import { ILaunchViewModel } from '../../components/launch/interface';
import { ILaunchControllerProps, IRouteLaunchArguments } from './interface';
import { injectConfigManifest } from '../../interface/config';
import mapping from './mapping';
import StateProcessor, {
    ILaunchState,
    IStateProcessorLaunchState,
    IStateProcessor,
    ILaunchEvent,
} from './state/events';
import { injectAssetFetcher } from '../../interface/asset';

const MainView = lazy(() => import('../../components/launch'));

type IProcessor = IStateProcessor<ILaunchState, ILaunchEvent, IStateProcessorLaunchState>;

function Render(props: ILaunchControllerProps): JSX.Element {
    const manifest = injectConfigManifest();
    const assets = injectAssetFetcher();

    const [processor, _] = useState<IProcessor>(StateProcessor(props.route));

    const onDownloadClick = () => processor.process(ILaunchEvent.UserRequestedDownloadingMedia);
    const onSkipClick = () => processor.process(ILaunchEvent.UserRequestedSkipMedia);

    const [state, setState] = useState<ILaunchViewModel>(
        mapping(onDownloadClick, onSkipClick, manifest, assets, processor),
    );

    const updateState = () =>
        setState(mapping(onDownloadClick, onSkipClick, manifest, assets, processor));

    processor.onStateChange(updateState);
    processor.onStatusChange(newStatus => {
        updateState();
        if (newStatus !== ILaunchState.Completed) {
            return;
        }
        props.didRequestLanguageSelect();
    });

    useEffect(() => {
        processor.process(ILaunchEvent.Start);
        return () => {
            processor.process(ILaunchEvent.Halt);
        };
    }, [props.route]);

    return (
        <Suspense fallback={null}>
            <MainView key="launch-vm" model={state} />
        </Suspense>
    );
}

const routesEqual = (prev: IRouteLaunchArguments, next: IRouteLaunchArguments) => {
    if (prev.db !== next.db) {
        return false;
    }
    if (prev.sessionName !== next.sessionName) {
        return false;
    }
    return true;
};

const propsEqual = (prev: ILaunchControllerProps, next: ILaunchControllerProps) => {
    return routesEqual(prev.route, next.route);
};

export default memo(Render, propsEqual) as typeof Render;
